import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMediaElementStatus } from "../../hooks/useMediaElementStatus";
import type { PlaybackState, StreamSlot } from "../../types/stream";
import type { WhepSession, WhepState } from "../../lib/whepSession";

export interface PipCardProps {
  slot: StreamSlot;
  session: WhepSession;
  state: WhepState;
  error?: string;
  label: string;
  /** Below the md breakpoint the card docks as a full-width strip. */
  compact: boolean;
  muted: boolean;
  canSwitch: boolean;
  onToggleMute(): void;
  onPrev(): void;
  onNext(): void;
  /** Absent when this browser cannot float this slot's media at all. */
  onPopOut?: () => void;
  onExpand(): void;
  onClose(): void;
  onDragHandlePointerDown?: (event: React.PointerEvent) => void;
  onToggleSize?: () => void;
  /** Reports what the media element is doing. PipCard never touches a store. */
  onPlaybackChange?: (playback: PlaybackState) => void;
}

const controlStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  background: "rgba(0,0,0,0.45)",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export default function PipCard({
  slot,
  session,
  state,
  error,
  label,
  compact,
  muted,
  canSwitch,
  onToggleMute,
  onPrev,
  onNext,
  onPopOut,
  onExpand,
  onClose,
  onDragHandlePointerDown,
  onToggleSize,
  onPlaybackChange,
}: PipCardProps) {
  const { t } = useTranslation();
  const mediaRef = useRef<HTMLVideoElement & HTMLAudioElement>(null);
  const isAudio = slot.streamContentType === "AUDIO";

  // No dep array on purpose: the media element is a NEW node whenever this
  // card is re-parented — most importantly when PipHost portals it into a
  // Document PiP window — while `session` stays the same object, so a
  // dependency-keyed effect would never rebind. The identity check keeps this
  // idempotent and avoids reassigning srcObject on a live element.
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;
    if (el.srcObject !== session.stream) el.srcObject = session.stream;
  });

  // After the srcObject effect, so the hook sees the stream when it calls play().
  const media = useMediaElementStatus(mediaRef, onPlaybackChange);

  return (
    <div
      data-testid="pip-card"
      data-compact={compact ? "true" : "false"}
      className="flex flex-col overflow-hidden rounded-lg border border-[var(--border)] bg-black shadow-lg"
      style={{ width: "100%" }}
    >
      <div
        data-testid="pip-header"
        onPointerDown={onDragHandlePointerDown}
        className="flex items-center gap-2 px-2 py-1 text-[11px] text-slate-300"
        style={{
          cursor: onDragHandlePointerDown ? "grab" : "default",
          touchAction: "none",
          userSelect: "none",
        }}
      >
        <span className="shrink-0">{label}</span>
        {slot.streamingUser && (
          <span className="truncate text-slate-100">
            {slot.streamingUser.name}
          </span>
        )}
      </div>

      <div className="relative" style={{ minHeight: isAudio ? 0 : undefined }}>
        {isAudio ? (
          <div className="flex items-center gap-2 px-2 py-2">
            {slot.streamingUser?.avatar && (
              <img
                data-testid="pip-avatar"
                src={slot.streamingUser.avatar}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full object-cover"
              />
            )}
            <audio
              data-testid="pip-audio"
              ref={mediaRef}
              autoPlay
              muted={muted}
              style={{ width: "100%" }}
            />
          </div>
        ) : (
          <video
            data-testid="pip-video"
            ref={mediaRef}
            autoPlay
            playsInline
            muted={muted}
            style={{ width: "100%", aspectRatio: "16/9", display: "block" }}
          />
        )}

        {(state === "connecting" || state === "reconnecting") && (
          <div
            data-testid="pip-status"
            className="absolute inset-0 flex items-center justify-center text-xs text-slate-400"
          >
            {t(state === "reconnecting" ? "pip_reconnecting" : "connecting")}
          </div>
        )}

        {state === "error" && (
          <div
            data-testid="pip-error"
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-2 text-center text-xs text-red-400"
          >
            <span>{error ?? t("connection_failed")}</span>
            <button
              data-testid="pip-retry"
              onClick={onExpand}
              className="rounded border border-[var(--border)] px-2 py-1 text-slate-200"
            >
              {t("retry")}
            </button>
          </div>
        )}

        {state === "playing" && (media.blocked || media.playback === "paused") && (
          <button
            data-testid="pip-tap-to-play"
            onClick={media.play}
            className="absolute inset-0 flex items-center justify-center text-xs text-white"
            style={{ background: "rgba(0,0,0,0.55)", border: "none", cursor: "pointer" }}
          >
            {t("tap_to_play", "Tap to play")}
          </button>
        )}

        {state === "playing" && !media.blocked && media.playback === "stalled" && (
          <div
            data-testid="pip-stalled"
            className="absolute inset-0 flex items-center justify-center text-xs text-slate-400"
          >
            {t("pip_reconnecting")}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-1 px-2 py-1">
        {canSwitch && (
          <>
            <button
              data-testid="pip-prev"
              onClick={onPrev}
              style={controlStyle}
              title={t("pip_previous_stream")}
              aria-label={t("pip_previous_stream")}
            >
              ‹
            </button>
            <button
              data-testid="pip-next"
              onClick={onNext}
              style={controlStyle}
              title={t("pip_next_stream")}
              aria-label={t("pip_next_stream")}
            >
              ›
            </button>
          </>
        )}
        <button
          data-testid="pip-mute"
          onClick={onToggleMute}
          style={controlStyle}
          title={t(muted ? "pip_unmute" : "pip_mute")}
          aria-label={t(muted ? "pip_unmute" : "pip_mute")}
        >
          {muted ? "🔇" : "🔊"}
        </button>
        {onToggleSize && (
          <button
            data-testid="pip-size"
            onClick={onToggleSize}
            style={controlStyle}
            title={t("pip_resize")}
            aria-label={t("pip_resize")}
          >
            ⇲
          </button>
        )}
        {onPopOut && (
          <button
            data-testid="pip-popout"
            onClick={onPopOut}
            style={controlStyle}
            title={t("pip_pop_out")}
            aria-label={t("pip_pop_out")}
          >
            ⧉
          </button>
        )}
        <button
          data-testid="pip-expand"
          onClick={onExpand}
          style={controlStyle}
          title={t("pip_expand")}
          aria-label={t("pip_expand")}
        >
          ⤢
        </button>
        <button
          data-testid="pip-close"
          onClick={onClose}
          style={controlStyle}
          title={t("pip_close")}
          aria-label={t("pip_close")}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
